/**
 * Detection of links inside chat message text.
 *
 * Used to stop non-approved members from posting URLs in the open chats
 * (malicious "course PDF" links were being spread through Day Trading With
 * Brian chat). Detection is deliberately broader than a strict URL parser:
 * it also catches defanged/obfuscated forms (hxxp, example[.]com) and bare
 * domains ("sharedfuturetech.com/uploads/x.pdf") that Sendbird would still
 * linkify for other members.
 *
 * It must NOT flag normal trading talk: prices ("450.50"), tickers ("BRK.B"),
 * times ("9.30"), percentages — none of those end in a real TLD.
 */

// Zero-width characters attackers use to split words while keeping the
// rendered text looking like a normal URL.
const ZERO_WIDTH_CODEPOINTS = [0x200b, 0x200c, 0x200d, 0x2060, 0xfeff];
const ZERO_WIDTH_RE = new RegExp(
  ZERO_WIDTH_CODEPOINTS.map((code) => String.fromCharCode(code)).join("|"),
  "g",
);

// example[.]com / example(dot)com / example{.}com → example.com
const OBFUSCATED_DOT_RE = /[[({]\s*(?:\.|dot)\s*[\])}]/gi;

// Explicit schemes (including defanged hxxp/hxxps) or a www. prefix.
const SCHEME_OR_WWW_RE = /(?:https?|hxxps?|ftps?):\/\/\S+|\bwww\.\S+/i;

// TLDs commonly seen in chat spam and legitimate link shares. A full IANA
// list would flag too much ("a.IT", "at 3.30 PM" is safe either way, but
// tickers/abbreviations collide with obscure ccTLDs), so keep this curated.
const COMMON_TLDS = [
  "com",
  "net",
  "org",
  "io",
  "co",
  "me",
  "app",
  "dev",
  "xyz",
  "info",
  "biz",
  "online",
  "site",
  "website",
  "link",
  "click",
  "live",
  "pro",
  "to",
  "gg",
  "ly",
  "be",
  "cc",
  "tv",
  "us",
  "uk",
  "ca",
  "au",
  "ru",
  "cn",
  "in",
  "de",
  "fr",
  "es",
  "it",
  "nl",
  "ch",
  "se",
  "no",
  "top",
  "store",
  "tech",
  "life",
  "world",
  "today",
  "club",
  "vip",
  "icu",
  "buzz",
  "space",
  "fun",
  "run",
  "pdf",
  "zip",
  "mov",
];

// Bare domain: one or more dot-separated labels ending in a known TLD,
// optionally followed by a path ("sharedfuturetech.com/uploads/7055/x.pdf").
const BARE_DOMAIN_RE = new RegExp(
  "\\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:" +
    COMMON_TLDS.join("|") +
    ")\\b(?:/\\S*)?",
  "i",
);

// Filenames with extensions used to deliver malware ("Course.pdf") — flagged
// even without a domain, since members are not allowed to share files either.
const RISKY_FILENAME_RE =
  /\S+\.(?:pdf|zip|rar|7z|exe|scr|bat|cmd|msi|apk|dmg|iso|lnk|hta|vbs|docm|xlsm|pptm)\b/i;

/**
 * Check whether free-form chat text contains a link (or something meant to
 * be reassembled into one).
 * @param {string} text - Raw message text
 * @returns {boolean}
 */
export const containsLink = (text) => {
  if (!text) return false;
  const normalized = String(text)
    .replace(ZERO_WIDTH_RE, "")
    .replace(OBFUSCATED_DOT_RE, ".");
  return (
    SCHEME_OR_WWW_RE.test(normalized) ||
    BARE_DOMAIN_RE.test(normalized) ||
    RISKY_FILENAME_RE.test(normalized)
  );
};

export const LINK_BLOCKED_MESSAGE =
  "Links can not be shared in chat. Only analysts and group leaders can post links.";
